import { CSSProperties, ReactNode } from 'react';
import PaperlogyFontFace from '@/features/main/v5/components/PaperlogyFontFace/PaperlogyFontFace';
import { panels, PanelName, prepareAsset } from './assets';
import * as S from './PrepareContent.styled';

function Panel({
    name,
    children,
    imageStyle,
    imageAlt = '',
}: {
    name: PanelName;
    children: ReactNode;
    imageStyle?: CSSProperties;
    imageAlt?: string;
}) {
    const { height, image } = panels[name];
    return (
        <S.Panel height={height}>
            <S.Background
                src={prepareAsset(`${name}-background.png`)}
                width={2560}
                height={height}
                alt=""
                loading={name === 'hero' ? 'eager' : 'lazy'}
            />
            <S.Canvas>
                {image && (
                    <S.Illustration
                        src={prepareAsset(image)}
                        width={1260}
                        alt={imageAlt}
                        style={imageStyle}
                        loading={name === 'hero' ? 'eager' : 'lazy'}
                    />
                )}
                {children}
            </S.Canvas>
        </S.Panel>
    );
}

function Caption({
    top,
    left,
    width,
    eyebrow,
    children,
    color,
    size = 24,
}: {
    top: number;
    left: number;
    width: number;
    eyebrow: string;
    children: ReactNode;
    color?: string;
    size?: number;
}) {
    return (
        <S.Copy top={top} left={left} width={width} size={18} color={color}>
            {eyebrow}
            <br />
            <strong style={{ fontSize: size }}>{children}</strong>
        </S.Copy>
    );
}

const introductionCards = [
    ['학원 현장에서 먼저 인정받은', '검증된 커리큘럼'],
    ['실제 학교수업, 평가기준을', '그대로 반영한 교과 콘텐츠'],
    ['전문학원식 영어프로그램으로', '영어 4대 영역 집중훈련'],
    ['문제집을 더하지 않아도 OK', '시중 수학 교재 문제유형 100% 반영'],
    ['모르는 문제는 알때까지', '1:1 무한 맞춤 처방'],
    ['학교 평가까지 걱정없이!', '최고 적중률의 평가문제 제공'],
    ['틀린 문제만 다시 골라', '효율적으로 채우는 오답노트'],
    ['오늘 공부 완료 시 초등생 맞춤', '포인트 제공, 동기부여 Up'],
] as const;

const mathCourses = [
    ['유형별 학습', '취약한 문제유형 집중 훈련'],
    ['필수문제 집중학습', '꼭 알아야 할 핵심문제 집중 훈련'],
    ['상위 심화반', '응용, 심화, 고난도까지 실력별 단계 확장'],
    ['서술형 학습', '풀이과정과 설명하는 힘까지'],
] as const;

const koreanCards = [
    ['전문 강사진의 교과서 완벽 분석,', '실전 문제를 통한 충분한 연습'],
    ['독해 학습과 문해력, 문법 등', '다양한 국어 학습 가능'],
    ['교과서 수록 어휘, 속담, 사자성어 등', '학습도 동시 제공'],
    ['부족한 부분은 지면 교재로', '한 번 더 풀며 연습'],
] as const;

const scienceCards = [
    ['‘왜 그렇게 되는지’ 핵심 개념', '이해 중심으로 예시와 함께 설명'],
    ['반복과 피드백으로 배운 개념', '완전 체득 프로세스'],
    ['개념 노트와 동영상 강의가 완벽히 매칭되어', '학습 집중도 극대화'],
    ['부족한 부분은 지면 교재로', '한번 더 풀며 연습'],
] as const;

const comparisonRows = [
    { label: '문제량', cats: '업계 최다 보유, 다양한\n유형의 문제 지속 제공', other: '제한된\n문제만 제공' },
    { label: '맞춤\n오답처방', cats: '모르는 문제, 알때까지\n무한 반복 처방', other: '틀린 문제\n한번만 다시 풀기' },
    { label: '수준별 / 유형별\n수학', cats: '수준별, 유형별\n맞춤코스 운영', other: '단순 인강 및\n문제집 풀이 위주' },
    {
        label: '수학 교재\n구매 필요성',
        cats: '최다 문제 보유, 별도\n문제집 구매없이 학습 가능',
        other: '추가 문제집 구매\n필요한 경우 있음',
    },
    {
        label: '화상코칭\n방식',
        cats: '발표하며 참여하는\n소규모 라이브클래스',
        catsNote: '(전문 수업 교안 활용)',
        other: '화상코칭 없거나\n선생님 대화 중',
        otherNote: '(전문 수업 교안 없음)',
        inlineNote: true,
    },
    { label: '시험\n대비', cats: '적중률 최고의\n기출문제 최다 제공', other: '기본 문제만\n제공' },
    {
        label: '학습기\n이용방식',
        cats: '부담없는 대여 방식',
        catsNote: '(윙크학습기 내에서 동시 이용)',
        other: '구매 필요',
        otherNote: '(해지 시 위약금에 포함)',
    },
    { label: '가격', cats: '추가금 없이 무료 이용', catsNote: '(윙크학습 이용 시)', other: '전과목\n12~14만원대' },
];

export default function PrepareContent() {
    return (
        <S.Content aria-label="초등교과 캐츠홈 소개">
            <PaperlogyFontFace />
            <section aria-labelledby="prepare-hero">
                <Panel name="hero">
                    <S.HeroBadge top={282} left={47} width={104} size={20} weight={700} color="white">
                        초등교과
                    </S.HeroBadge>
                    <S.Heading
                        as="h1"
                        id="prepare-hero"
                        top={335}
                        left={47}
                        width={640}
                        size={60}
                        leading={1.25}
                        align="left"
                        color="white"
                    >
                        유아 때 시작한 윙크,
                        <br />
                        <S.Green>초등</S.Green> 가면 더 강해집니다
                    </S.Heading>
                </Panel>
            </section>
            <section aria-labelledby="prepare-expertise">
                <Panel name="expertise">
                    <S.Copy top={80} size={34} weight={700}>
                        유아부터 고등까지 이어지는 교육전문성
                    </S.Copy>
                    <S.Heading as="h2" id="prepare-expertise" top={132} size={50} leading={1.3}>
                        그 노하우를 윙크의 <S.Green>초등학습에 담았습니다</S.Green>
                    </S.Heading>
                    {['유아\n1위', '초등\n1위', '학원\n1위', '중등\n최상위권', '고등\n최상위권'].map((label, index) => (
                        <S.Copy
                            key={label}
                            top={[398, 360, 326, 290, 255][index]}
                            left={121 + index * 214}
                            width={177}
                            size={24}
                            weight={800}
                            leading={1.2}
                            color="white"
                        >
                            {label}
                        </S.Copy>
                    ))}
                    <S.Copy top={642} size={16} color="#a6a6a6">
                        * 유아, 초등 신규가입자수 1위 (유아학습, 초등 영어 스마트학습 부문)
                    </S.Copy>
                    <S.Copy top={665} size={16} color="#a6a6a6">
                        * 2025 소비자선호도 1위 / 학원도입율 1위
                    </S.Copy>
                </Panel>
            </section>
            <section aria-labelledby="prepare-access">
                <Panel name="access">
                    <S.Heading as="h2" id="prepare-access" top={69} size={50} leading={1.3} color="white">
                        윙크 회원이라면
                        <br />
                        <S.Mint>초등 전문학습 ‘캐츠홈’</S.Mint> 동시 이용 가능
                    </S.Heading>
                    {['소비자\n선호도', '학원\n도입', '문제\n은행'].map((label, index) => (
                        <div key={label}>
                            <S.Award top={288} left={365 + index * 183} width={160} size={40} leading={1.3}>
                                {index === 0 ? '1위' : '최다'}
                            </S.Award>
                            <S.Copy
                                top={343}
                                left={365 + index * 183}
                                width={160}
                                size={16}
                                weight={600}
                                leading={1.3}
                                color="white"
                            >
                                {label}
                            </S.Copy>
                        </div>
                    ))}
                    <S.Copy top={788} size={16} color="rgba(255, 255, 255, 0.5)">
                        * 예비초 이상부터 키보드가 있는 스쿨북으로 무상 업그레이드
                    </S.Copy>
                </Panel>
            </section>
            <section aria-labelledby="prepare-introduction">
                <Panel name="introduction">
                    <S.Heading as="h2" id="prepare-introduction" top={89} size={50} leading={1.3}>
                        학교 공부는 탄탄하게, 아이 실력에 맞춰 더 깊게!
                    </S.Heading>
                    <S.Copy top={164} size={24}>
                        교과학습부터 심화, 평가대비까지 집에서 만나는 전문학원, 캐츠홈
                    </S.Copy>
                    {introductionCards.map(([eyebrow, title], index) => (
                        <S.Copy
                            key={title}
                            top={276 + [0, 360, 725, 1084][Math.floor(index / 2)]}
                            left={184 + (index % 2) * 458}
                            width={440}
                            size={20}
                            weight={600}
                            color="white"
                        >
                            {eyebrow}
                            <br />
                            <strong style={{ fontSize: 24, color: '#5eee94' }}>{title}</strong>
                        </S.Copy>
                    ))}
                    <S.Heading top={1903} size={45} leading={1.3}>
                        초등 전문학습 캐츠홈, 더 자세히 알아볼까요?
                    </S.Heading>
                </Panel>
            </section>
            <section aria-labelledby="prepare-math">
                <Panel name="math-title">
                    <S.Heading as="h2" id="prepare-math" top={75} size={50} leading={1.3} color="white">
                        잘하는 부분은 더 키우고
                        <br />
                        부족한 부분은 꼼꼼히 채우는 캐츠홈 수학
                    </S.Heading>
                </Panel>
                <Panel name="math-basics">
                    <S.Heading as="h3" top={60} size={40} leading={1.4}>
                        학교 수학은 탄탄하게
                    </S.Heading>
                    <S.Copy top={119}>교과 개념부터 학교 진도까지 초등 수학의 기본을 탄탄하게</S.Copy>
                    <Caption top={452} left={20} width={390} eyebrow="전문강사의 개념강의로">
                        쉽고 빠른 이해
                    </Caption>
                    <Caption top={452} left={435} width={390} eyebrow="교과 진도에 맞춘">
                        문제 훈련
                    </Caption>
                    <Caption top={452} left={850} width={390} eyebrow="학교평가까지 이어지는">
                        충분한 실전 문제 훈련
                    </Caption>
                </Panel>
                <Panel name="math-courses">
                    <S.Heading as="h3" top={60} size={40} leading={1.4}>
                        필요한 수학은 골라서 더 강하게
                    </S.Heading>
                    <S.Copy top={116}>부족한 영역은 더 집중하고 잘하는 영역은 더 높은 수준까지!</S.Copy>
                    {mathCourses.map(([title, description], index) => (
                        <div key={title}>
                            <S.Copy
                                top={199 + Math.floor(index / 2) * 360}
                                left={181 + (index % 2) * 463}
                                width={440}
                                size={24}
                                weight={800}
                                leading={1.05}
                                color="white"
                            >
                                {title}
                            </S.Copy>
                            <S.Copy
                                top={251 + Math.floor(index / 2) * 360}
                                left={181 + (index % 2) * 463}
                                width={440}
                                size={18}
                            >
                                {description}
                            </S.Copy>
                        </div>
                    ))}
                </Panel>
                <Panel name="math-prescription">
                    <S.Heading as="h3" top={55} size={40} leading={1.4}>
                        모르는 문제는 알 때까지, 끝까지!
                    </S.Heading>
                    <S.Copy top={115}>취약점 분석 후 필요한 문제를 무한 맞춤 처방</S.Copy>
                    <Caption top={451} left={20} width={390} eyebrow="학습결과를 분석해">
                        취약점 발견
                    </Caption>
                    <Caption top={451} left={435} width={390} eyebrow="틀린 유형은 유사문제로">
                        다시 도전
                    </Caption>
                    <Caption top={451} left={850} width={390} eyebrow="모르는 문제 무한 처방으로">
                        확실하게 보완
                    </Caption>
                </Panel>
                <Panel name="math-expert">
                    <S.Heading
                        as="h3"
                        top={71}
                        left={166}
                        width={600}
                        size={50}
                        leading={1.3}
                        align="left"
                        color="black"
                    >
                        공부의 신 <span style={{ color: 'white' }}>‘이병훈 소장’</span>
                    </S.Heading>
                    <S.Copy top={143} left={166} width={600} size={18} align="left" color="rgba(255, 255, 255, 0.8)">
                        서울과학고등학교 / 서울대학교 졸업
                    </S.Copy>
                    <S.Copy
                        as="blockquote"
                        top={225}
                        left={166}
                        width={600}
                        size={20}
                        leading={1.75}
                        align="left"
                        color="black"
                    >
                        아이들은 틀리는 문제를 계속 틀려요.
                        <br />그 문제가 아이에게 약한 유형의 문제입니다.
                        <br />
                        <strong>약점을 정확히 찾아 집중 반복</strong>시키는 것,
                        <br />
                        <strong>진짜 실력 향상의 핵심</strong>입니다.
                    </S.Copy>
                    <S.Copy top={390} left={166} width={600} size={20} align="left" color="black">
                        이 핵심을 가장 잘 반영한 프로그램,
                    </S.Copy>
                    <S.Heading top={435} left={166} width={600} size={40} leading={1.375} align="left" color="black">
                        <span style={{ color: 'white', textDecoration: 'underline', textUnderlineOffset: 8 }}>
                            바로 캐츠의
                        </span>
                        <br />
                        <span style={{ textDecoration: 'underline', textUnderlineOffset: 8 }}>실전형 수학입니다</span>
                    </S.Heading>
                </Panel>
            </section>
            <section aria-labelledby="prepare-subjects">
                <Panel name="subjects-title">
                    <S.Heading as="h2" id="prepare-subjects" top={75} size={50} leading={1.3} color="white">
                        학교 공부의 힘<br />
                        국어부터 사회, 과학까지 탄탄하게
                    </S.Heading>
                </Panel>
                <Panel name="korean">
                    <S.Heading as="h3" top={69} size={40} leading={1.4}>
                        교과서 이해부터 실전까지
                        <br />
                        <S.Pink>빈틈없이 잡는 국어</S.Pink>
                    </S.Heading>
                    {koreanCards.map(([eyebrow, title], index) => (
                        <Caption
                            key={title}
                            top={241 + Math.floor(index / 2) * 354}
                            left={184 + (index % 2) * 458}
                            width={440}
                            eyebrow={eyebrow}
                            size={18}
                        >
                            {title}
                        </Caption>
                    ))}
                </Panel>
                <Panel name="social-science">
                    <S.Heading as="h3" top={60} size={40} leading={1.4}>
                        <S.Pink>보고 끝나는 사회, 과학이 아닙니다</S.Pink>
                        <br />
                        개념 이해 → 문제적용 → 부족한 부분 보완까지
                        <br />
                        학교 공부에 필요한 실전 학습
                    </S.Heading>
                    {scienceCards.map(([eyebrow, title], index) => (
                        <Caption
                            key={title}
                            top={296 + Math.floor(index / 2) * 355}
                            left={184 + (index % 2) * 458}
                            width={440}
                            eyebrow={eyebrow}
                            size={18}
                        >
                            {title}
                        </Caption>
                    ))}
                </Panel>
            </section>
            <section aria-labelledby="prepare-exams">
                <Panel name="exams-title">
                    <S.Heading as="h2" id="prepare-exams" top={74} size={50} leading={1.3} color="white">
                        학교시험 완벽 대비
                        <br />
                        평가대비 집중학습 &amp; 오답노트
                    </S.Heading>
                </Panel>
                <Panel name="exams">
                    <S.Heading as="h3" top={69} size={40} leading={1.325}>
                        적중률 높은 기출예상문제로 충분히 연습하고
                        <br />
                        <S.Green>틀린 문제는 끝까지 추적해 다시 학습</S.Green>
                    </S.Heading>
                    <S.Copy top={240} left={211} width={440} size={24} weight={800} leading={1.1} color="white">
                        기출예상족보
                    </S.Copy>
                    <S.Copy top={240} left={664} width={440} size={24} weight={800} leading={1.1} color="white">
                        오답노트
                    </S.Copy>
                    <S.Copy top={292} left={184} width={440} size={18} leading={1.4}>
                        전문 강사진의 교과서 완벽 분석,
                        <br />
                        실전 문제를 통한 충분한 연습
                    </S.Copy>
                    <S.Copy top={292} left={642} width={440} size={18} leading={1.4}>
                        틀린 문제는 다시풀기,
                        <br />
                        취약 부분 맞춤 처방 및 필요한 학습만 선별
                    </S.Copy>
                    <S.Copy top={674} size={30} weight={800} color="#00239f">
                        <span style={{ textDecoration: 'underline', textUnderlineOffset: 5 }}>
                            실제 시험이 중요한 중고등 미리 대비
                        </span>
                    </S.Copy>
                </Panel>
            </section>
            <section aria-labelledby="prepare-teachers">
                <Panel name="teachers-title">
                    <S.Heading as="h2" id="prepare-teachers" top={74} size={50} leading={1.3} color="white">
                        아이 혼자 두지 않는 학습
                        <br />
                        교과 전문 선생님의 맞춤 관리
                    </S.Heading>
                </Panel>
                <Panel name="teachers">
                    <S.Heading as="h3" top={64} size={40} leading={1.375}>
                        학습 결과를 기반으로 부족한 부분을
                        <br />
                        <span style={{ color: '#005bdf' }}>한번 더 꼼꼼하게 관리</span>
                    </S.Heading>
                    {[
                        { top: 288, left: 693, align: 'left', eyebrow: '학습 데이터 기반', title: '약점 분석' },
                        { top: 528, left: 310, align: 'right', eyebrow: '이해 안 된 부분', title: '다시 체크' },
                        { top: 753, left: 693, align: 'left', eyebrow: '학습 결과', title: '분석 및 피드백' },
                        { top: 1002, left: 310, align: 'right', eyebrow: '시험 전', title: '핵심 개념 보완' },
                    ].map(({ top, left, align, eyebrow, title }, index) => (
                        <div key={title}>
                            <S.Step
                                top={top}
                                left={align === 'left' ? left : left + 178}
                                width={73}
                                size={16}
                                weight={800}
                                color="white"
                            >
                                STEP {index + 1}
                            </S.Step>
                            <S.Copy
                                top={top + 49}
                                left={left}
                                width={250}
                                size={20}
                                leading={1.5}
                                align={align as 'left' | 'right'}
                                color="#333"
                            >
                                {eyebrow}
                                <br />
                                <strong style={{ fontSize: 24 }}>{title}</strong>
                            </S.Copy>
                        </div>
                    ))}
                    <S.Copy
                        as="blockquote"
                        top={1498}
                        left={361}
                        width={340}
                        size={18}
                        weight={700}
                        leading={1.4}
                        align="left"
                    >
                        “ 필요한 핵심만 짚어주니 학원
                        <br />
                        &nbsp; 오고 가는 시간대비 매우 효율적이에요 ”
                    </S.Copy>
                    <S.Copy
                        as="blockquote"
                        top={1597}
                        left={586}
                        width={350}
                        size={18}
                        weight={700}
                        leading={1.4}
                        align="left"
                    >
                        “ 캐츠 라이브클래스 하다가 다른데는
                        <br />
                        &nbsp; 못갈 듯 싶네요. 저도, 아이도 눈이 높아져서 ”
                    </S.Copy>
                </Panel>
            </section>
            <section aria-labelledby="prepare-comparison">
                <Panel name="comparison">
                    <S.Heading as="h2" id="prepare-comparison" top={85} size={50} leading={1.3}>
                        비교할수록 보이는 <S.ComparisonHighlight>캐츠홈의 차이</S.ComparisonHighlight>
                    </S.Heading>
                    <S.Comparison aria-labelledby="prepare-comparison">
                        <colgroup>
                            <col />
                            <col />
                            <col />
                        </colgroup>
                        <thead>
                            <tr>
                                <th scope="col">체크 포인트</th>
                                <th scope="col">캐츠홈</th>
                                <th scope="col">시중 전과목 스마트학습</th>
                            </tr>
                        </thead>
                        <tbody>
                            {comparisonRows.map(({ label, cats, catsNote, other, otherNote, inlineNote }) => (
                                <tr key={label}>
                                    <th scope="row">{label}</th>
                                    <td>
                                        <S.ComparisonMark positive viewBox="0 0 40.13 40.13" aria-hidden="true">
                                            <circle cx="20.065" cy="20.065" r="18.567" />
                                            <circle cx="20.065" cy="20.065" r="11.5562" />
                                        </S.ComparisonMark>
                                        {cats}
                                        {catsNote && (
                                            <>
                                                {inlineNote ? ' ' : <br />}
                                                <small>{catsNote}</small>
                                            </>
                                        )}
                                    </td>
                                    <td>
                                        <S.ComparisonMark viewBox="0 0 47 47" aria-hidden="true">
                                            <path d="M22.685 8.75C23.045 8.05 23.955 8.05 24.315 8.75L39.595 35.15C39.955 35.85 39.505 36.55 38.785 36.55H8.215C7.495 36.55 7.045 35.85 7.405 35.15L22.685 8.75Z" />
                                        </S.ComparisonMark>
                                        {other}
                                        {otherNote && (
                                            <>
                                                {inlineNote ? ' ' : <br />}
                                                <small>{otherNote}</small>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </S.Comparison>
                </Panel>
            </section>
            <section aria-labelledby="prepare-closing">
                <Panel
                    name="closing"
                    imageStyle={{ clipPath: 'inset(0 0 58% 0)' }}
                    imageAlt="유아 때 잘 시작한 공부, 초등에서도 잘 이어져야 하니까"
                >
                    <S.Copy top={224} size={24} color="white">
                        기초를 다지는 <strong>윙크</strong>에서 초등 실력을 키우는 <strong>캐츠홈</strong>까지
                    </S.Copy>
                    <S.Heading
                        as="h2"
                        id="prepare-closing"
                        top={274}
                        size={50}
                        leading={1.3}
                        color="white"
                        style={{ textShadow: '0 4px 4px rgba(0, 0, 0, 0.3)' }}
                    >
                        아이의 성장에 맞춰 <S.Mint>윙크가 계속 함께합니다</S.Mint>
                    </S.Heading>
                </Panel>
            </section>
        </S.Content>
    );
}
